import {
    useEffect,
    useRef,
} from "react";

import {
    POLLING,
} from "./configuration";

export const POLLING_SPEED = Object.freeze({
    slow: "SLOW",
    medium: "MEDIUM",
    fast: "FAST",
});

const normalizeDelay = (value) => {
    const numericValue = Number(value);

    if (
        !Number.isFinite(numericValue)
        || numericValue <= 0
    ) {
        return null;
    }

    return numericValue;
};

export const getPollingDelay = (
    speed,
) => {
    if (
        typeof speed === "number"
    ) {
        return normalizeDelay(speed);
    }

    return normalizeDelay(
        POLLING[speed],
    );
};

export const getIncreasingPollingSpeed = ({
    endTimestamp,
    mediumThresholdMs,
    fastThresholdMs,
}) => {
    if (
        endTimestamp === null
        || endTimestamp === undefined
        || endTimestamp === ""
    ) {
        return POLLING_SPEED.slow;
    }

    const normalizedEndTimestamp =
        Number(endTimestamp);

    if (
        !Number.isFinite(
            normalizedEndTimestamp,
        )
    ) {
        return POLLING_SPEED.slow;
    }

    const remainingTime =
        normalizedEndTimestamp
        - Date.now();

    if (
        remainingTime
        <= fastThresholdMs
    ) {
        return POLLING_SPEED.fast;
    }

    if (
        remainingTime
        <= mediumThresholdMs
    ) {
        return POLLING_SPEED.medium;
    }

    return POLLING_SPEED.slow;
};

/*
 * Liefert die Wartezeit für adaptives
 * Polling direkt in Millisekunden.
 *
 * Ist eine Stufe deaktiviert, wartet der
 * Scheduler bis zum Beginn der nächsten
 * Stufe. Dadurch kann zum Beispiel SLOW=0
 * und MEDIUM=0 mit FAST=1_000 kombiniert
 * werden, ohne dass der Wechsel zu FAST
 * verloren geht.
 */
export const getIncreasingPollingDelay = ({
    endTimestamp,
    mediumThresholdMs,
    fastThresholdMs,
    pollingStartedAt = Date.now(),
}) => {
    if (
        endTimestamp === null
        || endTimestamp === undefined
        || endTimestamp === ""
    ) {
        return getPollingDelay(
            POLLING_SPEED.slow,
        );
    }

    const normalizedEndTimestamp =
        Number(endTimestamp);

    if (
        !Number.isFinite(
            normalizedEndTimestamp,
        )
    ) {
        return getPollingDelay(
            POLLING_SPEED.slow,
        );
    }

    const normalizedPollingStartedAt =
        Number(pollingStartedAt);

    const cadenceStart =
        Number.isFinite(
            normalizedPollingStartedAt,
        )
            ? normalizedPollingStartedAt
            : Date.now();

    const speed =
        getIncreasingPollingSpeed({
            endTimestamp:
                normalizedEndTimestamp,
            mediumThresholdMs,
            fastThresholdMs,
        });

    const configuredDelay =
        getPollingDelay(speed);

    if (configuredDelay !== null) {
        const nextThreshold =
            speed === POLLING_SPEED.slow
                ? mediumThresholdMs
                : speed
                    === POLLING_SPEED.medium
                    ? fastThresholdMs
                    : null;

        if (nextThreshold === null) {
            return configuredDelay;
        }

        return Math.min(
            configuredDelay,
            Math.max(
                0,
                normalizedEndTimestamp
                    - nextThreshold
                    - cadenceStart,
            ),
        );
    }

    if (
        speed === POLLING_SPEED.slow
    ) {
        return Math.max(
            0,
            normalizedEndTimestamp
                - mediumThresholdMs
                - cadenceStart,
        );
    }

    if (
        speed === POLLING_SPEED.medium
    ) {
        return Math.max(
            0,
            normalizedEndTimestamp
                - fastThresholdMs
                - cadenceStart,
        );
    }

    return null;
};

/*
 * Serialisiertes Polling mit stabiler
 * Start-zu-Start-Kadenz.
 *
 * poll muss ein Promise zurückgeben. Der
 * nächste Lauf wird erst geplant, nachdem
 * das Promise abgeschlossen ist. Dauert
 * ein Request länger als der konfigurierte
 * Abstand, startet der nächste Lauf direkt
 * danach, aber niemals parallel.
 * Optional kann poll eine Wartezeit in
 * Millisekunden für genau den nächsten
 * Lauf zurückgeben.
 */
export const usePolling = ({
    poll,
    enabled = true,
    immediate = false,
    speed = POLLING_SPEED.slow,
    getSpeed,
    getDelay,
    onError,
    restartKey,
}) => {
    const pollRef = useRef(poll);
    const speedRef = useRef(speed);
    const getSpeedRef = useRef(getSpeed);
    const getDelayRef = useRef(getDelay);
    const onErrorRef = useRef(onError);

    pollRef.current = poll;
    speedRef.current = speed;
    getSpeedRef.current = getSpeed;
    getDelayRef.current = getDelay;
    onErrorRef.current = onError;

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let isActive = true;
        let timeoutId = null;

        const resolveDelay = (
            startedAt = null,
        ) => {
            if (
                typeof getDelayRef.current
                === "function"
            ) {
                const dynamicDelay = Number(
                    getDelayRef.current({
                        pollingStartedAt:
                            startedAt
                            ?? Date.now(),
                    }),
                );

                return Number.isFinite(
                    dynamicDelay,
                )
                    && dynamicDelay >= 0
                    ? dynamicDelay
                    : null;
            }

            const currentSpeed =
                typeof getSpeedRef.current
                    === "function"
                    ? getSpeedRef.current()
                    : speedRef.current;

            return getPollingDelay(
                currentSpeed,
            );
        };

        const schedule = (
            startedAt = null,
            overrideDelay = null,
        ) => {
            if (!isActive) {
                return;
            }

            const normalizedOverride =
                Number(overrideDelay);

            const delay =
                overrideDelay !== null
                && Number.isFinite(
                    normalizedOverride,
                )
                && normalizedOverride >= 0
                    ? normalizedOverride
                    : resolveDelay(
                        startedAt,
                    );

            if (delay === null) {
                return;
            }

            const elapsed =
                startedAt === null
                    ? 0
                    : Date.now()
                        - startedAt;

            timeoutId = window.setTimeout(
                run,
                Math.max(0, delay - elapsed),
            );
        };

        const run = async () => {
            if (!isActive) {
                return;
            }

            const startedAt = Date.now();
            let nextDelay = null;

            try {
                nextDelay =
                    await pollRef.current({
                        isPollingActive: () => {
                            return isActive;
                        },
                    });
            } catch (error) {
                if (
                    isActive
                    && typeof onErrorRef.current
                        === "function"
                ) {
                    onErrorRef.current(error);
                }
            } finally {
                schedule(
                    startedAt,
                    nextDelay,
                );
            }
        };

        if (immediate) {
            void run();
        } else {
            schedule();
        }

        return () => {
            isActive = false;

            if (timeoutId !== null) {
                window.clearTimeout(
                    timeoutId,
                );
            }
        };
    }, [
        enabled,
        immediate,
        restartKey,
    ]);
};
